import { DateRangeType, LeadReportHeaderField, ReportType } from "src/types/experiences/leadsAndReports";
import type { ColumnDef } from "../common/AnalyticsTable";

export const TODAY: DateRangeType = "today";
export const YESTERDAY: DateRangeType = "yesterday";
export const SEVEN_DAYS: DateRangeType = "7Days";
export const FOURTEEN_DAYS: DateRangeType = "14Days";
export const WEEK_TO_DATE: DateRangeType = "WTD";
export const MONTH_TO_DATE: DateRangeType = "MTD";
export const THIRTY_DAYS: DateRangeType = "30Days";
export const NINETY_DAYS: DateRangeType = "90Days";
export const CUSTOM: DateRangeType = "custom";

export const DATE_RANGES: { label: DateRangeType; value: string }[] = [
  { label: TODAY, value: "Today" },
  { label: YESTERDAY, value: "Yesterday" },
  { label: SEVEN_DAYS, value: "7 Days" },
  { label: FOURTEEN_DAYS, value: "14 Days" },
  { label: WEEK_TO_DATE, value: "Week to Date" },
  { label: MONTH_TO_DATE, value: "Month to Date" },
  { label: THIRTY_DAYS, value: "30 Days" },
  { label: NINETY_DAYS, value: "90 Days" },
];

export const REPORT_TYPES: Record<ReportType, { label: string; value: ReportType }> = {
  ALL: { label: "All", value: "ALL" },
  PERSONALIZATION: { label: "Personalized", value: "PERSONALIZATION" },
  ABM: { label: "Campaign", value: "ABM" },
};

export const DEFAULT_REPORT_TYPE: ReportType = REPORT_TYPES.ALL.value;

export interface ReportSummaryTile {
  path: string;
  title: string;
  accessorKey: string;
}

export const REPORT_SUMMARY_LEFT: Record<ReportType, ReportSummaryTile[]> = {
  ALL: [
    { path: "/images/experiences/reports/sessions.svg", title: "Sessions Recorded", accessorKey: "sessions" },
    { path: "/images/experiences/reports/visitors.svg", title: "Visitors Attracted", accessorKey: "visitors" },
    { path: "/images/experiences/reports/leads.svg", title: "Leads Captured", accessorKey: "leads" },
    { path: "/images/experiences/reports/timespent.svg", title: "Total Time Spent", accessorKey: "totalTimeSpent" },
    { path: "/images/experiences/reports/average-time.svg", title: "Average Time Spent", accessorKey: "averageTimeSpent" },
  ],
  PERSONALIZATION: [
    { path: "/images/experiences/reports/leads.svg", title: "Personalised Lead Count", accessorKey: "leads" },
    { path: "/images/experiences/reports/sessions.svg", title: "Personalised Views Opened", accessorKey: "views" },
    { path: "/images/experiences/reports/visitors.svg", title: "Bounce Rate Percentage", accessorKey: "bounceRate" },
    { path: "/images/experiences/reports/timespent.svg", title: "Total Time Spent", accessorKey: "totalTimeSpent" },
    { path: "/images/experiences/reports/average-time.svg", title: "Average Time Spent", accessorKey: "averageTimeSpent" },
  ],
  ABM: [
    { path: "/images/experiences/reports/sessions.svg", title: "Views Number of Views", accessorKey: "views" },
    { path: "/images/experiences/reports/visitors.svg", title: "Visitors Total Number", accessorKey: "visitors" },
    { path: "/images/experiences/reports/leads.svg", title: "Leads Captured", accessorKey: "leads" },
    { path: "/images/experiences/reports/timespent.svg", title: "Total Time Spent", accessorKey: "totalTimeSpent" },
    { path: "/images/experiences/reports/average-time.svg", title: "Average Time Spent", accessorKey: "averageTimeSpent" },
  ],
};

// Base lead-report columns (custom fields get appended at runtime).
export const LEAD_REPORT_HEADER: LeadReportHeaderField[] = [
  { name: "First Name", field: "firstName", values: "", uid: "firstName" },
  { name: "Last Name", field: "lastName", values: "", uid: "lastName" },
  { name: "Email Id", field: "email", values: "", uid: "email" },
  { name: "Company", field: "company", values: "", uid: "company" },
  { name: "Title", field: "title", values: "", uid: "title" },
  { name: "Work Phone", field: "workPhone", values: "", uid: "workPhone" },
  { name: "Source", field: "source", values: "", uid: "campaignSource" },
  { name: "Medium", field: "medium", values: "", uid: "campaignMedium" },
  { name: "Campaign", field: "campaign", values: "", uid: "campaignName" },
  { name: "Created Date", field: "createdDate", values: "", uid: "createdDate" },
  { name: "Last Visited", field: "lastVisitedDate", values: "", uid: "lastVisitedDate" },
  { name: "Views", field: "views", values: "", uid: "views" },
  { name: "Time Spent", field: "timeSpent", values: "", uid: "timeSpent" },
  { name: "Completion", field: "completion", values: "", uid: "completion" },
];

// Reports → Asset Analytics table columns + CSV export headers.
export const ASSET_ANALYTICS_COLUMNS: ColumnDef[] = [
  { key: "asset", header: "Asset Name" },
  { key: "views", header: "Views", align: "right" },
  { key: "visitors", header: "Visitors", align: "right" },
  { key: "leads", header: "Leads", align: "right" },
  { key: "avg", header: "Avg. Time Spent", align: "right" },
  { key: "bounce", header: "Bounce Rate", align: "right" },
  { key: "downloads", header: "Downloads", align: "right" },
  { key: "trends", header: "Trends", align: "right-fill" },
];

export const ASSET_ANALYTICS_CSV_HEADERS: { label: string; key: string }[] = [
  { label: "Asset Title", key: "assetTitle" },
  { label: "Content-Type", key: "contentType" },
  { label: "Link to Asset", key: "assetLink" },
  { label: "Added Date", key: "addedDate" },
  { label: "Updated date", key: "updatedDate" },
  { label: "Views", key: "views" },
  { label: "Visitors", key: "visitors" },
  { label: "Leads", key: "leads" },
  { label: "Avg. Time Spent", key: "avg" },
  { label: "Bounce Rate", key: "bounce" },
  { label: "Downloads", key: "downloads" },
  { label: "Custom Field", key: "customField" },
  { label: "Tags", key: "tags" },
];

export const SESSION_CSV_HEADERS = [
  { label: "Visitor", key: "visitor" },
  { label: "Email", key: "email" },
  { label: "Last Visited Date", key: "lastVisitedDate" },
  { label: "Source", key: "source" },
  { label: "Medium", key: "medium" },
  { label: "Page Landed on", key: "landedPage" },
  { label: "City", key: "city" },
  { label: "State", key: "state" },
  { label: "Country", key: "country" },
  { label: "Device", key: "device" },
  { label: "Browser", key: "browser" },
  { label: "Time Spent", key: "time_spent" },
  { label: "Completion", key: "completion" },
];

export const INTERACTIVE_CONTENT_TYPES = [
  "INTERACTIVE_CONTENT",
  "INTERACTIVE_CONTENT_V2",
];

export const SLIDE_STORYBOARDS = ["SPATIAL", "FLIP_BOOK"];

export const isInteractive = (type?: string) =>
  !!type && INTERACTIVE_CONTENT_TYPES.includes(type);
